import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AppSessionProvider } from "./context/AppSessionContext";
import AuthenticatedLayout from "./layout/AuthenticatedLayout";
import { readErrorMessage } from "./lib/http";
import {
  ConnectionState,
  createBackendConnection,
} from "./lib/backendConnection";
import { useDomLocalization } from "./useDomLocalization";
import ClientCompaniesListPage from "./pages/ClientCompaniesListPage";
import ClientCompanyEditPage from "./pages/ClientCompanyEditPage";
import ClientCompanyCreatePage from "./pages/ClientCompanyCreatePage";
import CreateEpisodePage from "./pages/CreateEpisodePage";
import CreateEpisodeDetailsPage from "./pages/CreateEpisodeDetailsPage";
import EpisodeDetailsPage from "./pages/EpisodeDetailsPage";
import {
  AuthMeResponse,
  canMaintainEmployees as canMaintainEmployeesByRole,
  canManageClientCompanies as canManageClientCompaniesByRole,
  canManageUsers as canManageUsersByRole,
  NO_ACCESS_ERROR,
} from "./types/auth";
import NoAccessPage from "./pages/NoAccessPage";
import UsersPage from "./pages/UsersPage";
import EmployeeCreatePage from "./pages/EmployeeCreatePage";
import EmployeesListPage from "./pages/EmployeesListPage";
import EmployeesMissingNamePage from "./pages/EmployeesMissingNamePage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import EmployeeEditPage from "./pages/EmployeeEditPage";
import FollowUpsPage from "./pages/FollowUpsPage";
import ReportsPage from "./pages/ReportsPage";

function App() {
  useDomLocalization();

  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout: auth0Logout,
    user,
    getAccessTokenSilently,
  } = useAuth0();

  const [me, setMe] = useState<AuthMeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("ready");
  const [profileRetry, setProfileRetry] = useState(0);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";
  const auth0Audience =
    process.env.REACT_APP_AUTH0_AUDIENCE || "https://api.your-app.com";

  const backend = useMemo(
    () =>
      createBackendConnection(
        backendUrl.replace(/\/$/, ""),
        setConnectionState,
      ),
    [backendUrl],
  );

  const logout = () =>
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });

  const authorizedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Audience,
          scope: "openid profile email",
        },
      });

      const headers = new Headers(init?.headers ?? {});
      headers.set("Authorization", `Bearer ${token}`);

      return backend.fetch(path, {
        ...init,
        headers,
      });
    },
    [auth0Audience, backend, getAccessTokenSilently],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setMe(null);
      setMeLoading(false);
      setMeError(null);
      setNoAccess(false);
      return;
    }

    let cancelled = false;

    const loadMe = async () => {
      setMeLoading(true);
      setMeError(null);

      try {
        const response = await authorizedFetch("/auth/me");

        if (response.status === 403) {
          const body = (await response
            .clone()
            .json()
            .catch(() => null)) as { error?: string } | null;
          if (body?.error === NO_ACCESS_ERROR) {
            if (!cancelled) {
              setNoAccess(true);
            }
            return;
          }
        }

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              `Failed to load current user (${response.status}).`,
            ),
          );
        }

        const payload = (await response.json()) as AuthMeResponse;

        if (!cancelled) {
          setMe(payload);
        }
      } catch (loadError) {
        if (!cancelled) {
          setMe(null);
          const message =
            loadError instanceof Error
              ? loadError.message
              : "Failed to load current user.";
          setMeError(message);
        }
      } finally {
        if (!cancelled) {
          setMeLoading(false);
        }
      }
    };

    void loadMe();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, isAuthenticated, profileRetry]);

  const canManageClientCompanies = canManageClientCompaniesByRole(
    me?.roles ?? [],
  );
  const canMaintainEmployees = canMaintainEmployeesByRole(me?.roles ?? []);
  const canManageUsers = canManageUsersByRole(me?.roles ?? []);

  const sessionContextValue = useMemo(
    () => ({
      backendUrl,
      authorizedFetch,
      me,
      meLoading,
      meError,
      canManageClientCompanies,
      canMaintainEmployees,
      canManageUsers,
    }),
    [
      backendUrl,
      authorizedFetch,
      me,
      meLoading,
      meError,
      canManageClientCompanies,
      canMaintainEmployees,
      canManageUsers,
    ],
  );

  if (isLoading) {
    return (
      <main className="page loadingPage">
        <p>Checking your session...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    void loginWithRedirect();
    return (
      <main className="page loadingPage">
        <p>Redirecting to login...</p>
      </main>
    );
  }

  if (noAccess) {
    return <NoAccessPage email={user?.email} onLogout={logout} />;
  }

  if (!me) {
    return (
      <main className="page loadingPage" aria-busy={!meError}>
        {meError ? (
          <>
            <h1>Unable to connect</h1>
            <p role="alert">{meError}</p>
            <button
              className="primaryButton"
              onClick={() => {
                setMeError(null);
                setProfileRetry((attempt) => attempt + 1);
              }}
            >
              Try again
            </button>
          </>
        ) : (
          <div role="status" aria-live="polite">
            <h1>
              {connectionState === "waking"
                ? "Waking up the server…"
                : "Connecting…"}
            </h1>
            <p>The server may be asleep. This can take up to two minutes.</p>
          </div>
        )}
      </main>
    );
  }

  return (
    <AppSessionProvider value={sessionContextValue}>
      <Toaster position="bottom-right" richColors closeButton />
      {connectionState === "waking" ? (
        <div
          className="serverConnectionNotice"
          role="status"
          aria-live="polite"
        >
          Waking up the server… Your request will continue when it is ready.
        </div>
      ) : null}
      {connectionState === "unavailable" ? (
        <div className="serverConnectionNotice" role="alert">
          The server is unavailable. Your work is still here. Please try your
          action again.
        </div>
      ) : null}
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <Routes>
          <Route
            element={
              <AuthenticatedLayout
                email={user?.email ?? "unknown user"}
                onLogout={logout}
                canManageClientCompanies={canManageClientCompanies}
                canMaintainEmployees={canMaintainEmployees}
                canManageUsers={canManageUsers}
                meLoading={meLoading}
                meError={meError}
              />
            }
          >
            <Route path="/" element={<Navigate to="/follow-ups" replace />} />
            <Route path="/intake" element={<CreateEpisodePage />} />
            <Route path="/intake/new" element={<CreateEpisodeDetailsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/follow-ups" element={<FollowUpsPage />} />
            <Route path="/episodes/:id" element={<EpisodeDetailsPage />} />
            <Route path="/employees/new" element={<EmployeeCreatePage />} />
            <Route
              path="/employees/missing-name"
              element={<EmployeesMissingNamePage />}
            />
            <Route path="/employees" element={<EmployeesListPage />} />
            <Route path="/employees/:id" element={<EmployeeDetailPage />} />
            <Route path="/employees/:id/edit" element={<EmployeeEditPage />} />
            <Route
              path="/client-companies/new"
              element={<ClientCompanyCreatePage />}
            />
            <Route
              path="/client-companies"
              element={<ClientCompaniesListPage />}
            />
            <Route
              path="/client-companies/:id/edit"
              element={<ClientCompanyEditPage />}
            />
            <Route path="/users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppSessionProvider>
  );
}

export default App;
